import type { LauncherShape } from './enums.js';

/**
 * Launcher silhouettes, shared here — rather than duplicated in the widget and
 * the admin portal — for the same reason `ICON_PRESETS` is: the admin picker
 * and the widget renderer must agree on the exact same set. The portal cannot
 * import `@feedback/widget`, so anything they both need lives in this package
 * or it drifts.
 *
 * The five built-in shapes deliberately do NOT share one CSS mechanism — two
 * are radii, two are clips, and one changes the layout box rather than the
 * silhouette. That is why this resolves to a recipe rather than a value.
 */

/**
 * Horizontal offset of the parallelogram's lean, in px.
 *
 * px and not a percentage: `clip-path` polygon points are percentages of the
 * reference box, so a percentage offset would change the ANGLE every time the
 * label changed length. A fixed offset holds the angle steady instead.
 *
 * 8px across the launcher's 34px (text-only) to 44px (icon-only) height gives
 * 10.3-13.2 degrees, bracketing the `-12deg` of `.tw-skew`, the brand's
 * active-nav chip (`apps/portal/src/styles/tailwind.css:270`). Matching 12
 * exactly would need the offset to track the height, which `clip-path` cannot
 * express — and the residual variance is well below the point where the two
 * read as different angles.
 */
export const PARALLELOGRAM_OFFSET_PX = 8;

/** Side of the 45-degree chamfer on `NOTCH`, echoing `.tw-diagonal`. */
export const NOTCH_SIZE_PX = 12;

/**
 * The launcher's elevation, in both the forms it has to take.
 *
 * `box-shadow` paints the BORDER BOX, so a clipped launcher would keep a
 * rectangular shadow hanging behind its angled edges. `drop-shadow` traces the
 * actual alpha silhouette, which is what a clipped or masked shape needs. The
 * blur radii differ because they are not the same unit: `drop-shadow` takes a
 * Gaussian standard deviation, roughly half a `box-shadow` blur for a matching
 * result.
 */
export const LAUNCHER_SHADOW = {
  box: '0 2px 8px rgba(0, 0, 0, 0.24)',
  drop: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.24))',
} as const;

/**
 * CSS `transition` value for the shape backdrop (`Button.tsx`'s
 * `backdropMotionStyle`, `ButtonPreview.tsx`'s function of the same name):
 * `background-color` fades the hover bg, `filter`/`box-shadow` fade the
 * elevation switch a live shape change causes. Lives here, not in either
 * renderer, because both need it byte-identical and the portal cannot import
 * `@feedback/widget` — the same reason `LAUNCHER_SHADOW` lives here. (It also
 * keeps the literal string `box-shadow` out of the portal's guarded
 * `apps/portal/src` tree, where a mechanical test greps source text for
 * Tailwind shadow *utility classes* and can't distinguish those from a CSS
 * property name spelled inside a `transition` value.)
 */
export const BACKDROP_TRANSITION =
  'background-color 0.15s ease, filter 0.15s ease, box-shadow 0.15s ease';

/**
 * MIME types allowed for a custom launcher mask.
 *
 * SVG first because it is the right answer: the mask is stretched to the
 * launcher's box, and a raster mask at a ~40px launcher needs 2x for retina,
 * leaves an anti-aliased translucent fringe against the customer's page, and
 * softens further under any stretch. PNG is allowed because customers have it.
 */
export const ALLOWED_SHAPE_MIME_TYPES: readonly string[] = ['image/svg+xml', 'image/png'];

/** A silhouette, not artwork. 64 KB is generous for either format. */
export const MAX_SHAPE_ASSET_BYTES = 64 * 1024;

export interface ShapeStyle {
  /**
   * Spread onto the SHAPE BACKDROP's inline style — never onto the `<button>`.
   * These are paint-only properties (clip, mask, radius) and they clip every
   * descendant, which is exactly why the backdrop exists: the launcher's
   * tooltip is a child of the button that renders OUTSIDE the button's box,
   * and clipping the button would delete it.
   */
  backdropStyle: Record<string, string>;
  /**
   * Merged into the `<button>`'s inline style. Layout-affecting properties
   * only — the backdrop is `inset: 0` and has no size of its own, so an
   * `aspect-ratio` there would do nothing.
   */
  buttonStyle: Record<string, string>;
  /** Extra horizontal padding (px per side) so a label clears an angled edge. */
  extraPaddingX: number;
  /** The silhouette is cut, so elevation must be `drop-shadow`, not `box-shadow`. */
  clipped: boolean;
  /** Fixed aspect — cannot carry a label. Callers must force icon-only rendering. */
  iconOnly: boolean;
  /**
   * Whether `opts.borderRadius` actually shows up anywhere in this shape's
   * styles. True only for `RECTANGLE` and `SQUARE`: `clip-path` and
   * `mask-image` ignore `border-radius` outright (`PARALLELOGRAM`, `NOTCH`,
   * `CUSTOM`), and `TEARDROP` supplies its own fixed `'50% 50% 50% 0'` rather
   * than reading the configured value. Neither `clipped` nor `iconOnly` alone
   * captures this — `TEARDROP` is `iconOnly` but not `clipped`, and still
   * doesn't honour the radius. Callers (the admin form) use this to disable
   * the radius input rather than let it appear to work.
   */
  honoursRadius: boolean;
}

/**
 * Characters that would terminate the `url()` token or the declaration it sits
 * in. The mask url is built from our own API base plus an application slug, so
 * a hit here means something is badly wrong upstream — but this string is
 * interpolated into CSS, and an unescaped quote in CSS is an injection
 * primitive, not a cosmetic bug.
 */
const CSS_URL_UNSAFE = /["'()\\;{}]|\s/;

function isSafeCssUrl(url: string): boolean {
  return url.length > 0 && !CSS_URL_UNSAFE.test(url);
}

/**
 * Resolves a launcher shape into inline styles for the button and its shape
 * backdrop. Pure — same inputs, same output, no DOM access — so both renderers
 * and the tests can call it freely.
 *
 * `borderRadius` is honoured only by the shapes that HAVE a corner radius
 * (`RECTANGLE`, `SQUARE`) — see `ShapeStyle.honoursRadius`. `clip-path` and
 * `mask-image` ignore `border-radius` outright, and `TEARDROP` supplies its
 * own fixed radius, so passing one to any of those would be a silent no-op —
 * the admin form disables the radius input for those shapes rather than
 * letting it appear to work.
 */
export function resolveShape(
  shape: LauncherShape,
  opts: { borderRadius: number; maskUrl?: string | null },
): ShapeStyle {
  const base = { extraPaddingX: 0, clipped: false, iconOnly: false, honoursRadius: false };

  switch (shape) {
    case 'SQUARE':
      return {
        ...base,
        backdropStyle: { borderRadius: `${opts.borderRadius}px` },
        buttonStyle: { aspectRatio: '1' },
        iconOnly: true,
        honoursRadius: true,
      };

    case 'PARALLELOGRAM':
      return {
        ...base,
        backdropStyle: {
          clipPath: `polygon(${PARALLELOGRAM_OFFSET_PX}px 0, 100% 0, calc(100% - ${PARALLELOGRAM_OFFSET_PX}px) 100%, 0 100%)`,
        },
        buttonStyle: {},
        // Both slants eat into the label's line, so give it room on each side.
        extraPaddingX: PARALLELOGRAM_OFFSET_PX,
        clipped: true,
      };

    case 'NOTCH':
      return {
        ...base,
        backdropStyle: {
          clipPath: `polygon(0 0, 100% 0, 100% calc(100% - ${NOTCH_SIZE_PX}px), calc(100% - ${NOTCH_SIZE_PX}px) 100%, 0 100%)`,
        },
        buttonStyle: {},
        clipped: true,
      };

    case 'TEARDROP':
      return {
        ...base,
        // The square corner sits bottom-left, pointing back into the page.
        backdropStyle: { borderRadius: '50% 50% 50% 0' },
        buttonStyle: { aspectRatio: '1' },
        iconOnly: true,
      };

    case 'CUSTOM': {
      const url = opts.maskUrl ?? '';
      // No asset yet, or an unusable one: fall back rather than paint nothing.
      // An empty or broken mask makes the launcher INVISIBLE, which is worse
      // than the wrong shape — the customer loses the feature silently.
      if (!isSafeCssUrl(url)) return resolveShape('RECTANGLE', opts);

      const image = `url("${url}")`;
      return {
        ...base,
        backdropStyle: {
          maskImage: image,
          WebkitMaskImage: image,
          // Stretch to the box: the shape is icon-only, so the box is a fixed
          // square and nothing actually distorts.
          maskSize: '100% 100%',
          WebkitMaskSize: '100% 100%',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
        },
        buttonStyle: { aspectRatio: '1' },
        clipped: true,
        iconOnly: true,
      };
    }

    case 'RECTANGLE':
    default:
      return {
        ...base,
        backdropStyle: { borderRadius: `${opts.borderRadius}px` },
        buttonStyle: {},
        honoursRadius: true,
      };
  }
}
